import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';

import { TranslatePipe } from '@recruitee/i18n';
import { IntegrationKinds, OfferPromoteJobBoardsHotspot } from '@recruitee/integrations-types';
import { LocationBrief } from '@recruitee/locations-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  RemoveHTMLTagsPipe,
} from '@recruitee/user-interface';

import {
  Account,
  CurrentHotspotToken,
  IntegrationKindToken,
} from '../../../client/integrations-client.module';
import { Destroyable, HttpErrorHandlerService, takeUntilDestroyed } from '../../../common';
import { IntegrationEventActions } from '../../../common/events/events';
import { ErrorMessagesWrapperComponent } from '../../../misc/error-messages-wrapper/error-messages-wrapper.component';
import { GFEA_HELP_ARTICLE_URL } from '../constants';
import { GfeaApiService } from '../gfea-api';
import {
  GfeaPostingFormComponent,
  initPrefilledPostingForm,
  postingFormValue,
} from '../gfea-posting-form/gfea-posting-form.component';
import { Company, CreatePostingForm, Offer, Posting } from '../types';

type DialogData = {
  offer: Offer;
  company: Company;
  careersOfferUrl: string;
  prefilledLocation: LocationBrief;
};

@Component({
  selector: 'rti-gfea-create-posting-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gfea-create-posting-dialog.component.html',
  styleUrls: ['./gfea-create-posting-dialog.component.less'],
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ErrorMessagesWrapperComponent,
    GfeaPostingFormComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class GfeaCreatePostingDialogComponent implements DialogComponentI<DialogData, boolean> {
  public creating: boolean = false;
  public errors: string[] = [];

  public suggestedOffers: Offer[] | null = null;

  public postingForm: UntypedFormGroup;
  public dialogData: DialogData;

  public GFEA_HELP_ARTICLE_URL = GFEA_HELP_ARTICLE_URL;

  constructor(
    public dialog: DialogRef,
    private account: Account,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private cdRef: ChangeDetectorRef,
    private gfeaApi: GfeaApiService,
    private store: Store,
    @Inject(CurrentHotspotToken)
    private currentHotspot: OfferPromoteJobBoardsHotspot,
    @Inject(IntegrationKindToken)
    private integrationKind: IntegrationKinds,
    private removeHtmlTagsPipe: RemoveHTMLTagsPipe,
  ) {}

  public setDialogData(data: DialogData): void {
    this.dialogData = data;
    this.postingForm = initPrefilledPostingForm({
      admin: this.account.admin,
      company: data.company,
      offer: data.offer,
      preparedDescription: this.removeHtmlTagsPipe.transform(
        `${data.offer.description}\n\n${data.offer.requirements}`,
      ),
      prefilledLocation: data.prefilledLocation,
    });
    this.cdRef.detectChanges();

    this.postingForm.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.cdRef.detectChanges());

    this.loadSuggestedOffers();
  }

  public publish(): void {
    this.creating = true;
    this.cdRef.detectChanges();

    this.gfeaApi.post('posting', this.createPostingForm).subscribe(
      (_data: { posting: Posting }) => {
        this.creating = false;
        this.cdRef.detectChanges();

        this.store.dispatch(
          IntegrationEventActions.JobBoardPostingCreated({
            payload: {
              context: { hotspot: this.currentHotspot, integrationKind: this.integrationKind },
            },
          }),
        );

        this.dialog.close({ success: true });
      },
      ({ error }) => {
        this.creating = false;
        this.errors = error.error;
        this.cdRef.detectChanges();
      },
    );
  }

  public close(): void {
    this.dialog.close();
  }

  public get formValid(): boolean {
    return this.postingForm.valid;
  }

  private get createPostingForm(): CreatePostingForm {
    return {
      offerId: this.currentHotspot.params.offerId,
      ...postingFormValue(this.postingForm),
    };
  }

  private loadSuggestedOffers(): void {
    this.gfeaApi.get('offers').subscribe(
      (data: { offers: Offer[] }) => {
        this.suggestedOffers = data.offers.filter(offer => offer.id !== this.dialogData.offer.id);
        this.cdRef.detectChanges();
      },
      errorResponse => {
        this.httpErrorHandlerService.default(errorResponse);
      },
    );
  }
}
